import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview";
import { nextOpenRowId } from "@/lib/attributeReview/reviewGroups";
import {
  isApprovedAttributeReviewStatus,
  isRejectedAttributeReviewStatus,
} from "@/lib/attributeReview/reviewQueueStatus";

const ROW_PARAM = "row";

export function useReviewDrawerRow(
  rows: AttributeReviewMappingRow[],
  orderedIds: string[],
  settledIds: ReadonlySet<string>,
  expandedId: string | null,
  openRow: (id: string | null) => void,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get(ROW_PARAM);
  const seen = useRef<{ param: string | null; id: string | null }>({
    param: null,
    id: null,
  });
  useEffect(() => {
    const before = seen.current;
    seen.current = { param, id: expandedId };
    if (param !== before.param && param !== expandedId) {
      openRow(param);
    } else if (expandedId !== before.id && expandedId !== param) {
      const next = new URLSearchParams(searchParams);
      if (expandedId) next.set(ROW_PARAM, expandedId);
      else next.delete(ROW_PARAM);
      setSearchParams(next, { replace: !(expandedId && !param) });
    }
  }, [param, expandedId, openRow, searchParams, setSearchParams]);

  const index = expandedId === null ? -1 : orderedIds.indexOf(expandedId);
  const row =
    expandedId === null
      ? undefined
      : rows.find((r) => String(r.id) === expandedId);
  const prevId = index > 0 ? orderedIds[index - 1] : null;
  const nextId = index >= 0 ? (orderedIds[index + 1] ?? null) : null;
  const afterSettleId =
    expandedId === null
      ? null
      : nextOpenRowId(orderedIds, expandedId, settledIds);
  const openIds = orderedIds.filter((id) => !settledIds.has(id));
  const openAt = expandedId === null ? -1 : openIds.indexOf(expandedId);
  const positionText =
    openAt >= 0
      ? `${openAt + 1} of ${rows.length - settledIds.size} open`
      : isRejectedAttributeReviewStatus(row?.decisionStatus)
        ? "Ignored"
        : isApprovedAttributeReviewStatus(row?.decisionStatus)
          ? "Approved"
          : "";
  return { row, prevId, nextId, afterSettleId, positionText };
}
